import React, { useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import { Upload } from 'lucide-react';
import { v4 as uuidv4 } from 'uuid';
import { supabase } from '../lib/db';
import toast from 'react-hot-toast';
import { useLanguage } from '../contexts/LanguageContext';
import { ACCEPTED_MEDIA_TYPES, MAX_VIDEO_SIZE } from '../utils/mediaUtils';

interface GalleryUploadProps {
  providerId: string;
  onUploadComplete: () => void;
}

export const GalleryUpload: React.FC<GalleryUploadProps> = ({ 
  providerId, 
  onUploadComplete 
}) => {
  const [uploading, setUploading] = React.useState(false);
  const { t } = useLanguage(); 
  const onDrop = useCallback(async (acceptedFiles: File[]) => {
    const file = acceptedFiles[0];
    if (!file || !providerId) return;
  
    try {
      setUploading(true);
  
      // First verify provider exists
      const { data: provider, error: providerError } = await supabase
        .from('provider_profiles')
        .select('id')
        .eq('user_id', providerId)
        .limit(1)
        .maybeSingle();
  
      if (providerError || !provider) {
        throw new Error('Provider profile not found');
      }
  
      // Upload image
      const fileExt = file.name.split('.').pop();
      const fileName = `${uuidv4()}.${fileExt}`;
      const filePath = `gallery/${fileName}`;
  
      const { error: uploadError } = await supabase.storage
        .from('images')
        .upload(filePath, file, {
          cacheControl: '3600',
          upsert: true,
        });
  
      if (uploadError) throw uploadError;
  
      // Get public URL
      const { data: { publicUrl } } = supabase.storage
        .from('images')
        .getPublicUrl(filePath);
  
      // Add to gallery table
      const { error: dbError } = await supabase
        .from('provider_gallery')
        .insert({
          provider_id: providerId,
          image_url: publicUrl,
        });
  
      if (dbError) {
        // If database insert fails, clean up the uploaded file
        await supabase.storage
          .from('images')
          .remove([filePath]);
        throw dbError;
      }
  
      toast.success(t('imageUploadSuccess'));
  
      // Call the onUploadComplete callback with the uploaded image URL
      onUploadComplete(publicUrl);
    } catch (error: any) {
      console.error('Upload error:', error);
      toast.error(t('imageUploadError'));
    } finally {
      setUploading(false);
    }
  }, [providerId, onUploadComplete]);
  
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: ACCEPTED_MEDIA_TYPES,
    maxFiles: 1,
    multiple: false,
    maxSize: MAX_VIDEO_SIZE, // 50MB to accommodate videos
    disabled: uploading || !providerId
  });

  if (!providerId) {
    return (
      <div className="text-center p-4 bg-red-50 text-red-600 rounded-lg">
        {t('pleaseSaveProfile')}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div
        {...getRootProps()}
        className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors
          ${isDragActive ? 'border-primary-500 bg-primary-50' : 'border-gray-300 hover:border-primary-500'}
          ${uploading ? 'opacity-50 cursor-not-allowed' : ''}`}
      >
        <input {...getInputProps()} />
        <Upload className="w-8 h-8 mx-auto text-gray-400 mb-2" />
        <p className="text-sm text-gray-600">
          {isDragActive ? t('dropImageHere') : t('dragDropOrClick')}
        </p>
        <p className="text-xs text-gray-500 mt-1">

        {t('maxFileSize')}
        </p>
      </div>

      {uploading && (
        <div className="flex items-center justify-center">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600"></div>
          <span className="ml-2 text-sm text-gray-600">{t('uploading')}</span>
        </div>
      )}
    </div>
  );
};